import { useCallback, useMemo } from "react";
import { List, Grid } from "@chakra-ui/react";
import { gql } from "__generated__/gql";
import { useBrandQuery } from "utils/apollo/hooks/brand";
import { useCurrentBrandId } from "utils/currentUserHooks";
import { trackEvent } from "utils/segment";
import { CreatedBrandDocument } from "utils/files/uploads/BrandDocumentFileUpload";
import FolderDocumentUpload from "./FolderDocumentUpload";
import FolderDocumentCard from "./FolderDocumentCard";

const documentsQuery = gql(/* GraphQL */ `
  query BrandDocumentsForFolder(
    $brandId: UUID!
    $slug: String!
    $filter: BrandDocumentFilter
  ) {
    documentFolder: documentFolderBySlug(slug: $slug) {
      id
      name
      slug
      brandDocuments: brandDocumentsList(
        condition: { brandId: $brandId }
        filter: $filter
        orderBy: CREATED_AT_DESC
      ) {
        id
        originalFilename
        mimeType
        isArchived
      }
    }
  }
`);

type Props = {
  isArchivedIncluded?: boolean;
  slug: string;
};

export default function FolderDocuments({ isArchivedIncluded, slug }: Props) {
  const brandId = useCurrentBrandId();

  const filter = useMemo(
    () => (isArchivedIncluded ? undefined : { isArchived: { equalTo: false } }),
    [isArchivedIncluded],
  );

  const { data } = useBrandQuery(documentsQuery, {
    fetchPolicy: "cache-and-network",
    variables: { slug, brandId: brandId as string, filter },
    skip: !slug || !brandId,
  });
  const {
    id: folderId,
    name: folderName,
    slug: folderSlug,
    brandDocuments,
  } = data?.documentFolder || {};

  const handleUploaded = useCallback(
    (brandDocument: CreatedBrandDocument) => {
      const {
        fileSize,
        id: brandDocumentId,
        mimeType,
        originalFilename,
      } = brandDocument;
      trackEvent("Document Added", {
        brandDocumentId,
        fileSize,
        folderId,
        folderName,
        folderSlug,
        mimeType,
        originalFilename,
      });
    },
    [folderId, folderName, folderSlug],
  );

  return (
    <Grid
      as={List}
      gap={4}
      gridTemplateColumns="repeat(auto-fill,minmax(auto, 9.5rem))"
    >
      {folderId && (
        <FolderDocumentUpload
          key={folderId}
          folderId={folderId}
          onUploaded={handleUploaded}
        />
      )}

      {brandDocuments?.map((doc) => (
        <FolderDocumentCard
          key={doc.id}
          documentId={doc.id}
          filename={doc.originalFilename}
          isArchived={doc.isArchived}
        />
      ))}
    </Grid>
  );
}
